import React, { useEffect, useState, useRef, forwardRef } from 'react';
/**
 * ClockRenderer — Analog/digital clock reading with time input.
 *
 * DDD co-locatie: moved from components/practice/renderers/ in s67.
 */
import { QuestionRendererProps } from './types';
import { AnalogClock } from '../../../visuals';
import { Clock, Sun, Moon } from 'lucide-react';
import { error as logError } from '../../../utils/logger';
export function ClockRenderer({
  question,
  onAnswer,
  onReadyChange,
  shouldValidate
}: QuestionRendererProps) {
  const clockConfig = question.data?.clock;
  const prevValidateRef = useRef(false);
  const targetHours = clockConfig?.target?.hours || 0;
  const targetMinutes = clockConfig?.target?.minutes || 0;
  const targetSeconds = 0;
  const format = clockConfig?.target?.format || '24h';
  const mode = clockConfig?.mode || 'read_analog_fill';
  const showDigitalClock = mode === 'read_digital_fill';
  const timeOfDay = targetHours >= 6 && targetHours < 18 ? 'day' : 'night';
  const TimeIcon = timeOfDay === 'day' ? Sun : Moon;
  const showSeconds = targetSeconds !== 0;
  const [hours, setHours] = useState('');
  const [minutes, setMinutes] = useState('');
  const [seconds, setSeconds] = useState('');
  const hoursInputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    setHours('');
    setMinutes('');
    setSeconds('');
    prevValidateRef.current = false;
    hoursInputRef.current?.focus();
  }, [question.id]);
  useEffect(() => {
    if (!shouldValidate) prevValidateRef.current = false;
  }, [shouldValidate]);
  useEffect(() => {
    const isReady = hours !== '' && minutes !== '' && (targetSeconds === 0 ? true : seconds !== '');
    onReadyChange?.(isReady);
  }, [hours, minutes, seconds, targetSeconds, onReadyChange]);
  useEffect(() => {
    if (shouldValidate && !prevValidateRef.current) {
      prevValidateRef.current = true;
      const h = hours.padStart(2, '0');
      const m = minutes.padStart(2, '0');
      const answerStr = showSeconds ? `${h}:${m}:${seconds.padStart(2, '0')}` : `${h}:${m}`;
      onAnswer(answerStr);
    }
  }, [shouldValidate, hours, minutes, seconds, showSeconds, onAnswer]);
  // Guard: missing config — rendered AFTER all hooks
  if (!clockConfig || !clockConfig.target) {
    logError('ClockRenderer: Missing clock config in question.data.clock');
    return <div className="text-center p-8 bg-red-50 rounded-xl border-2 border-red-200" data-id="element-7073">
        <p className="text-red-700 font-bold" data-id="element-7074">⚠️ Klok configuratie ontbreekt</p>
        <p className="text-sm text-red-600 mt-2" data-id="element-7075">
          Deze vraag heeft geen geldige klok data.
        </p>
      </div>;
  }
  const handleInputChange = (setter: React.Dispatch<React.SetStateAction<string>>, value: string, max: number) => {
    if (value === '' || /^\d+$/.test(value) && parseInt(value) <= max) {
      setter(value);
    }
  };
  return <div className="flex flex-col items-center w-full max-w-2xl mx-auto space-y-6" data-id="element-7076">
      {/* Time of Day Indicator */}
      <div className="flex items-center gap-3 bg-gradient-to-r from-indigo-50 to-purple-50 px-6 py-3 rounded-2xl border-2 border-indigo-200 shadow-sm" data-id="element-7077">
        <TimeIcon className={`w-6 h-6 ${timeOfDay === 'day' ? 'text-amber-500' : 'text-indigo-400'}`} data-id="element-7078" />
        <span className="text-base font-bold text-slate-700" data-id="element-7079">
          {timeOfDay === 'day' ? 'Overdag (06:00 - 18:00)' : "'s Nachts (18:00 - 06:00)"}
        </span>
      </div>

      {/* Clock Display */}
      {showDigitalClock ? <div className="p-8 bg-gradient-to-br from-slate-800 to-slate-900 rounded-3xl border-4 border-slate-700 shadow-2xl" data-id="element-7080">
          <div className="font-mono text-6xl font-black text-emerald-400 tracking-wider" data-id="element-7081">
            {targetHours.toString().padStart(2, '0')}:
            {targetMinutes.toString().padStart(2, '0')}
          </div>
        </div> : <div className="p-6 bg-gradient-to-br from-slate-50 to-slate-100 rounded-3xl border-2 border-slate-200 shadow-xl" data-id="element-7082">
          <AnalogClock hours={targetHours} minutes={targetMinutes} seconds={targetSeconds} showSeconds={showSeconds} size={180} data-id="element-7083" />
        </div>}

      {/* Time Input */}
      <div className="w-full bg-white p-6 rounded-2xl border-2 border-indigo-100 shadow-lg" data-id="element-7084">
        <div className="flex items-center justify-between mb-5" data-id="element-7085">
          <div className="flex items-center gap-2" data-id="element-7086">
            <div className="p-2 bg-indigo-50 rounded-xl" data-id="element-7087">
              <Clock className="w-5 h-5 text-indigo-600" data-id="element-7088" />
            </div>
            <span className="text-sm font-bold text-slate-500 uppercase tracking-wide" data-id="element-7089">
              Vul de tijd in
            </span>
          </div>
          <div className="px-3 py-1.5 bg-indigo-50 rounded-full border border-indigo-200" data-id="element-7090">
            <span className="text-xs font-bold text-indigo-700 uppercase tracking-wide" data-id="element-7091">
              {format === '24h' ? '24-uurs' : '12-uurs'}
            </span>
          </div>
        </div>

        <div className="flex items-center justify-center gap-3" data-id="element-7092">
          <TimeInputField ref={hoursInputRef} label="Uur" value={hours} onChange={v => handleInputChange(setHours, v, 23)} data-id="element-7093" />
          <div className="text-3xl font-black text-slate-400 mt-8" data-id="element-7094">:</div>
          <TimeInputField label="Min" value={minutes} onChange={v => handleInputChange(setMinutes, v, 59)} data-id="element-7095" />
          {showSeconds && <>
              <div className="text-3xl font-black text-slate-400 mt-8" data-id="element-7096">:</div>
              <TimeInputField label="Sec" value={seconds} onChange={v => handleInputChange(setSeconds, v, 59)} data-id="element-7097" />
            </>}
        </div>

        <p className="text-center text-slate-500 text-sm mt-4 font-medium" data-id="element-7098">
          Typ de tijd zoals je die op de klok ziet
        </p>
      </div>
    </div>;
}
/** Small reusable time input field to reduce JSX repetition. */
const TimeInputField = forwardRef<HTMLInputElement, {
  label: string;
  value: string;
  onChange: (v: string) => void;
}>(({
  label,
  value,
  onChange
}, ref) => <div className="flex flex-col items-center" data-id="element-7099">
    <label className="text-xs font-bold text-slate-500 mb-2 uppercase tracking-wide" data-id="element-7100">
      {label}
    </label>
    <input ref={ref} type="text" inputMode="numeric" pattern="[0-9]*" value={value} onChange={e => onChange(e.target.value)} placeholder="00" className="w-16 h-16 text-center text-2xl font-black rounded-2xl border-2 border-slate-200 focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100 outline-none transition-all placeholder:text-slate-300 bg-slate-50" maxLength={2} data-id="element-7101" />
  </div>);